import { Search, X } from "lucide-react"

import { cn } from "../../lib/utils"

// `md` lines up with an adjacent `h-9` Combobox trigger; `sm` is the compact
// rhythm for dense `text-xs` toolbars (segmented tabs, filter pills).
type SearchInputSize = "sm" | "md"

type SearchInputProps = {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  ariaLabel?: string
  size?: SearchInputSize
  className?: string
}

const INPUT_SIZE: Record<SearchInputSize, string> = {
  sm: "h-7 pl-8 pr-8 text-xs",
  md: "h-9 pl-9 pr-9 text-sm",
}

const ICON_POS: Record<SearchInputSize, string> = {
  sm: "left-2.5",
  md: "left-3",
}

/** Controlled text search box: leading magnifier, trailing clear button when
 *  non-empty. Styling matches the Combobox trigger so filters read as one set. */
export function SearchInput({
  value,
  onChange,
  placeholder = "Search…",
  ariaLabel,
  size = "md",
  className,
}: SearchInputProps) {
  return (
    <div className={cn("relative flex items-center", className)}>
      <Search
        className={cn("pointer-events-none absolute size-4 text-muted-foreground", ICON_POS[size])}
      />

      <input
        type="text"
        aria-label={ariaLabel}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className={cn(
          "w-full rounded-full border border-border bg-card shadow-card outline-none transition-[border-color,box-shadow,background-color] duration-200 hover:border-foreground/15",
          INPUT_SIZE[size],
          "placeholder:text-muted-foreground/60 focus-visible:ring-2 focus-visible:ring-ring/30"
        )}
      />

      {value ? (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => onChange("")}
          className="absolute right-2 inline-flex size-5 items-center justify-center rounded text-muted-foreground transition-colors hover:text-foreground"
        >
          <X className="size-3.5" />
        </button>
      ) : null}
    </div>
  )
}
